/* eslint-disable indent */
/* eslint-disable array-bracket-newline */
import { connect, useDispatch } from 'react-redux';
import { enums, IPriceAlert, ITicker } from '@kattana-io/common';
import {
  formatMobileTableData,
  IMobileTableScema,
} from '../../../helpers/formatters';
import TableCardMobile from '../../../molecules/Table/mobile/TableCard';
import { ModalTypes } from '../../../reducers/modal';
import { actionShowModal } from '../../../actions/modal';
import TickerCell from '../TickerCell';
import PriceAlertsActions from './Actions';
import Badge from '../../../molecules/Badge';
import Loader from '../../common/Loader';
import { IStore } from '../../../reducers';
import { IAuthState } from '../../../reducers/auth';
import { decoratePriceAlertStatus } from '../../../helpers/decorators';
import TableHeaderMobile from '../../../molecules/Table/mobile/TableHeader';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { alertPriceDifferenceFormatter } from './Table';

type tickersMap = Record<string, ITicker>;

interface IPriceAlertsTableMobileProps {
  tickers: tickersMap;
  alerts: any[];
  loading: boolean;
  filter: string;
  onFilter(filter: string): void;
  auth: IAuthState;
}

const statusToBadgeKlass = (status: string): any => {
  switch (status) {
    case enums.PriceAlertStatus.WATCH:
      return 'green';
    case enums.PriceAlertStatus.CANCELED:
      return 'red';
    default:
      return 'grey';
  }
};

const getColorForItem = (row: any) => {
  if (row.status === enums.PriceAlertStatus.WATCH) {
    return 'green';
  }
  return 'grey';
};

const PriceAlertsTableMobile = ({
  tickers,
  alerts,
  filter,
  onFilter,
  loading,
  auth,
}: IPriceAlertsTableMobileProps) => {
  const dispatch = useDispatch();
  const placeholderText = auth.loggedIn
    ? 'No data found'
    : 'Connect wallet to get access to price alerts';

  const onPriceEdit = (alert: IPriceAlert) => {
    if (alert.status === enums.PriceAlertStatus.WATCH) {
      dispatch(actionShowModal(ModalTypes.priceAlertEdit, { alert }));
    }
  };

  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Pair',
        accessor: 'ticker',
        parser: (_, row) => {
          const { chain, pair } = row;
          const key = `${chain}-${pair}`;
          const ticker = tickers[key];
          return <TickerCell path={key} ticker={ticker} />;
        },
        width: 140,
      },
      item_2: {
        name: 'Alert Price',
        accessor: 'alert_price',
        parser: (_, row) => (
          <NumberDisplay
            value={row.price_usd || row.price}
            endSymbol={row.price_usd ? ' $' : undefined}
            string
          />
        ),
        onClick: (row) => onPriceEdit(row),
        string: true,
      },
    },
    row_2: {
      item_1: {
        name: 'Difference',
        accessor: 'difference',
        parser: (_, row) => {
          const { chain, pair } = row;
          const key = `${chain}-${pair}`;
          const ticker = tickers[key];
          const priceDiff = alertPriceDifferenceFormatter(row, ticker);
          return (
            <>
              {priceDiff.percent && (
                <NumberDisplay
                  value={priceDiff.percent}
                  round={2}
                  startSymbol='('
                  endSymbol='%)'
                  string
                />
              )}
              &nbsp;
              <NumberDisplay
                value={priceDiff.value}
                round={2}
                endSymbol={priceDiff.sign}
                string
              />
            </>
          );
        },
        width: 140,
        string: true,
      },
      item_2: {
        name: 'Status',
        accessor: 'status',
        parser: (value) => (
          <Badge
            variant='fill'
            klass={statusToBadgeKlass(value)}
            text={decoratePriceAlertStatus(value)}
          />
        ),
      },
    },
    row_3: {
      item_1: {
        name: 'Exchange',
        accessor: 'exchange',
        width: 140,
      },
      item_2: {
        name: 'Actions',
        accessor: 'actions',
        parser: (_, row) => {
          const { chain, pair } = row;
          const key = `${chain}-${pair}`;
          return (
            <PriceAlertsActions
              alert={row}
              filterEnabled={key === filter}
              onFilter={onFilter}
            />
          );
        },
        width: 120,
      },
    },
  };

  return (
    <div className='dex_trades_mobile__table'>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !alerts.length ? (
        <div className='centrify'>{placeholderText}</div>
      ) : (
        <>
          <TableHeaderMobile schema={schema} />
          {formatMobileTableData({
            data: alerts,
            schema,
            getColorForItem,
          })?.map((row, index) => (
            <TableCardMobile key={index} data={row} />
          ))}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: IStore) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(PriceAlertsTableMobile);
